import { useEffect, useContext, createContext, useMemo, useCallback, useRef } from 'react';

import { StoreCode } from 'schema/storeCode';
import { useStoreValue } from 'state/hooks/useStore';
import { useForceUpdate } from '../hooks/useForceUpdate';

interface Context {
  store: StoreCode;
}

type UnsubscribeCallback = () => void;

interface PersistStorage {
  get(key: string): string | null;
  set(key: string, value: string | null): void;
  remove(key: string): void;
  subscribe(key: string, callback: () => void): UnsubscribeCallback;
  destroy(): void;
}

const serverPersistStorage: PersistStorage = {
  get: () => null,
  set: () => {},
  remove: () => {},
  subscribe: () => () => {},
  destroy: () => {}
};

const makePersistStorage = (context: Context): PersistStorage => {
  if (typeof window === 'undefined') {
    return serverPersistStorage;
  }

  const eventTarget = new EventTarget();
  const prefix = `ag:${context.store}`;
  const getFullKey = (key: string) => `${prefix}:${key}`;

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key?.startsWith(prefix)) {
      eventTarget.dispatchEvent(new CustomEvent(event.key));
    }
  };

  window.addEventListener('storage', handleStorageChange);

  const get = (key: string) => localStorage.getItem(getFullKey(key));

  return {
    get,

    set(key, value) {
      if (get(key) === value) {
        return;
      }

      const fullKey = getFullKey(key);

      if (value) {
        localStorage.setItem(fullKey, value);
      } else {
        localStorage.removeItem(fullKey);
      }

      eventTarget.dispatchEvent(new CustomEvent(fullKey));
    },

    remove(key) {
      const fullKey = getFullKey(key);

      localStorage.removeItem(fullKey);
      eventTarget.dispatchEvent(new CustomEvent(fullKey));
    },

    subscribe(key, callback: () => void) {
      const fullKey = getFullKey(key);

      eventTarget.addEventListener(fullKey, callback);

      return () => {
        eventTarget.removeEventListener(fullKey, callback);
      };
    },

    destroy() {
      window.removeEventListener('storage', handleStorageChange);
    }
  };
};

const PersistStorageContext = createContext<PersistStorage | null>(null);

interface PersistStorageProviderProps {
  children: React.ReactNode;
}

export const PersistStorageProvider = ({ children }: PersistStorageProviderProps) => {
  const store = useStoreValue();
  const persistStorage = useMemo(() => makePersistStorage({ store }), [store]);
  const prevPersistStorageRef = useRef(persistStorage);

  useEffect(() => {
    if (persistStorage !== prevPersistStorageRef.current) {
      prevPersistStorageRef.current.destroy();
    }

    prevPersistStorageRef.current = persistStorage;
  }, [persistStorage]);

  return (
    <PersistStorageContext.Provider value={persistStorage}>
      {children}
    </PersistStorageContext.Provider>
  );
};

const usePersistStorage = () => {
  const persistStorage = useContext(PersistStorageContext);

  if (!persistStorage) {
    throw new Error('usePersistStorage must be used within a PersistStorageProvider');
  }

  return persistStorage;
};

export const usePersistStorageState = (key: string) => {
  const persistStorage = usePersistStorage();
  const valueRef = useRef(persistStorage.get(key));
  const mountedRef = useRef(true);
  const forceUpdate = useForceUpdate();

  const getValue = useCallback(
    () => (mountedRef.current ? valueRef.current : persistStorage.get(key)),
    [persistStorage, key]
  );

  const setValue = useCallback(
    (newValue: string | null) => {
      valueRef.current = newValue;
      persistStorage.set(key, newValue);
    },
    [persistStorage, key]
  );

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    const newValue = persistStorage.get(key);

    if (newValue !== valueRef.current) {
      valueRef.current = newValue;
      forceUpdate();
    }

    let didCancel = false;

    const unsubscribe = persistStorage.subscribe(key, () => {
      if (!didCancel) {
        // We use ref so that we have the ability to get the actual value,
        // not the value from the closure.
        valueRef.current = persistStorage.get(key);
        // Because of the use of ref, we need to initiate the rendering of the component manually
        forceUpdate();
      }
    });

    return () => {
      didCancel = true;
      unsubscribe();
    };
  }, [persistStorage, key, forceUpdate]);

  return [getValue, setValue] as const;
};
